import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, FileText, Award, Calculator, TrendingUp, Download, Mail, Search } from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import PayslipPreviewModal from '../../../components/payslip/PayslipPreviewModal';

interface EmployeePayslip {
  id: string;
  name: string;
  staffNumber: string;
  department: string;
  employmentStatus: string;
  netPay: number;
  payslipStatus: string;
  basicSalary: number;
  allowances: number;
  deductions: number;
}

const mockEmployees: EmployeePayslip[] = [
  {
    id: 'EMP001',
    name: 'Ahmed Al-Mansouri',
    staffNumber: 'EMP001',
    department: 'Engineering',
    employmentStatus: 'Full-time',
    netPay: 12500.0,
    payslipStatus: 'Generated',
    basicSalary: 10000.0,
    allowances: 3000.0,
    deductions: 500.0,
  },
  {
    id: 'EMP002',
    name: 'Fatima Al-Zahra',
    staffNumber: 'EMP002',
    department: 'HR',
    employmentStatus: 'Full-time',
    netPay: 8750.0,
    payslipStatus: 'Generated',
    basicSalary: 7500.0,
    allowances: 1500.0,
    deductions: 250.0,
  },
  {
    id: 'EMP003',
    name: 'Mohammed Hassan',
    staffNumber: 'EMP003',
    department: 'Finance',
    employmentStatus: 'Part-time',
    netPay: 4200.0,
    payslipStatus: 'Pending',
    basicSalary: 4000.0,
    allowances: 300.0,
    deductions: 100.0,
  },
  {
    id: 'EMP004',
    name: 'Aisha Abdullah',
    staffNumber: 'EMP004',
    department: 'Engineering',
    employmentStatus: 'Full-time',
    netPay: 11200.0,
    payslipStatus: 'Generated',
    basicSalary: 9500.0,
    allowances: 2200.0,
    deductions: 500.0,
  },
  {
    id: 'EMP005',
    name: 'Omar Al-Rashid',
    staffNumber: 'EMP005',
    department: 'Marketing',
    employmentStatus: 'Contract',
    netPay: 6800.0,
    payslipStatus: 'Generated',
    basicSalary: 6000.0,
    allowances: 1000.0,
    deductions: 200.0,
  },
];

const sections = [
  { id: 'payslips', label: 'Payslips', icon: FileText },
  { id: 'certificates', label: 'Salary Certificates', icon: Award },
  { id: 'eos-payouts', label: 'EOS Payout Slips', icon: Calculator },
  { id: 'variance-reports', label: 'Variance Reports', icon: TrendingUp },
];

const payrollCycles = ['November 2024', 'October 2024', 'September 2024', 'August 2024'];

export default function PayrollDocumentsCenter() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('payslips');
  const [filters, setFilters] = useState({
    department: '',
    employmentStatus: '',
    payrollCycle: 'November 2024',
  });
  const [employees, setEmployees] = useState<EmployeePayslip[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeePayslip | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewPayslip = (employee: EmployeePayslip) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  useEffect(() => {
    setEmployees(mockEmployees);
  }, []);

  const filteredEmployees = employees.filter((emp) => {
    const matchesDepartment = !filters.department || emp.department === filters.department;
    const matchesStatus = !filters.employmentStatus || emp.employmentStatus === filters.employmentStatus;
    return matchesDepartment && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Generated':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<FolderOpen className="w-6 h-6" />}
        title="Payroll Documents Center"
        description="Payroll Documents Center is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-blue-500"
        gradientTo="to-cyan-500"
      />

      {/* Page Header */}
      <div className="bg-card rounded-2xl shadow-sm border border-border p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('Payroll Documents Center')}</h1>
              <p className="text-gray-600 mt-1">{t('Comprehensive document management for all payroll materials')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>{t('System Online')}</span>
            </div>
            <div className="h-4 w-px bg-gray-300"></div>
            <span>{t('Last updated: 2 minutes ago', { nsSeparator: false })}</span>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="bg-muted rounded-xl p-4 border border-border">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-700" />
              <select
                value={filters.department}
                onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
                className="px-3 py-2 border border-border rounded-lg text-sm bg-card"
              >
                <option value="">{t('All Departments')}</option>
                <option value="Engineering">{t('Engineering')}</option>
                <option value="HR">{t('HR')}</option>
                <option value="Finance">{t('Finance')}</option>
                <option value="Marketing">{t('Marketing')}</option>
              </select>
            </div>
            <select
              value={filters.employmentStatus}
              onChange={(e) => setFilters((prev) => ({ ...prev, employmentStatus: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm bg-card"
            >
              <option value="">{t('All Statuses')}</option>
              <option value="Full-time">{t('Full-time')}</option>
              <option value="Part-time">{t('Part-time')}</option>
              <option value="Contract">{t('Contract')}</option>
            </select>
            <select
              value={filters.payrollCycle}
              onChange={(e) => setFilters((prev) => ({ ...prev, payrollCycle: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm bg-card"
            >
              {payrollCycles.map((cycle) => (
                <option key={cycle} value={cycle}>
                  {cycle}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden mb-6">
        <div className="border-b border-border">
          <nav className="flex space-x-0">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-3 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                    activeSection === section.id
                      ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <SectionIcon className="w-5 h-5" />
                  <span>{t(section.label)}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Section Content */}
        <div className="p-8">
          {activeSection === 'payslips' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">{t('Employee Payslips')}</h3>
                <div className="flex gap-2">
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg"
                   
                  >
                    <Download className="w-4 h-4" />
                    {t('Bulk Download')}
                  </button>
                  <button
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
                   
                  >
                    <Mail className="w-4 h-4" />
                    {t('Email Payslips')}
                  </button>
                </div>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Employee')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Department')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Basic Salary')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Allowances')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Deductions')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Net Pay')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                          <p className="text-xs text-gray-500">{emp.staffNumber}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{emp.department}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">AED {emp.basicSalary.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-green-600">+AED {emp.allowances.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-red-600">-AED {emp.deductions.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">AED {emp.netPay.toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(emp.payslipStatus)}`}>
                          {t(emp.payslipStatus)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewPayslip(emp)}
                            className="p-1 text-gray-700 hover:text-blue-600"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeSection === 'certificates' && (
            <div className="text-center py-12">
              <Award className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('Salary Certificates')}</h3>
              <p className="text-gray-500">{t('Generate and manage salary certificates for employees')}</p>
              <button
                className="mt-4 px-6 py-2 bg-emerald-500 text-white rounded-lg"
               
              >
                {t('Generate Certificate')}
              </button>
            </div>
          )}

          {activeSection === 'eos-payouts' && (
            <div className="text-center py-12">
              <Calculator className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('EOS Payout Slips')}</h3>
              <p className="text-gray-500">{t('View and download end-of-service payout documentation')}</p>
              <button
                className="mt-4 px-6 py-2 bg-emerald-500 text-white rounded-lg"
               
              >
                {t('Generate EOS Slip')}
              </button>
            </div>
          )}

          {activeSection === 'variance-reports' && (
            <div className="text-center py-12">
              <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('Variance Reports')}</h3>
              <p className="text-gray-500">{t('Period-over-period payroll variance analysis')}</p>
              <button
                className="mt-4 px-6 py-2 bg-emerald-500 text-white rounded-lg"
               
              >
                {t('Generate Variance Report')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payslip Preview Modal */}
      <PayslipPreviewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        payslip={selectedEmployee ? {
          id: Number(selectedEmployee.id) || 0,
          name: selectedEmployee.name,
          staffNumber: selectedEmployee.staffNumber,
          department: selectedEmployee.department,
          basicSalary: selectedEmployee.basicSalary,
          variablePay: selectedEmployee.allowances,
          deductions: selectedEmployee.deductions,
          netPay: selectedEmployee.netPay,
          payslipStatus: selectedEmployee.payslipStatus,
        } : null}
        payrollCycle={filters.payrollCycle}
      />
    </div>
  );
}
